/**
 * Parse SMSWiser drop ids from SIEM action path and detail bodies.
 */

import { extractSmswiserKillContextFromSiem } from '@/features/siem/utils/extract-smswiser-kill-context';

import {
  bulkPreparedContextFromDetails,
  collapseSinglePickTarget,
  editBatchContextFromDetails,
  mergeCtx,
} from './siem-resolve-pick-targets.util';
import type { DropTimelineContext } from './types';

const UID_RE = /^[A-Za-z0-9-]{4,64}$/;

const RESERVED_DROP_PATH_SEGMENTS = new Set(
  ['kill', 'edit', 'edit-batch', 'find', 'all', 'by-unique-id'].map((s) =>
    s.toLowerCase(),
  ),
);

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

export function parseTimelineUniqueId(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  if (!UID_RE.test(t)) return undefined;
  if (/^\d+$/.test(t)) return undefined;
  return t;
}

export function parseDropId(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 1) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isInteger(n) && n >= 1) return n;
  }
  return undefined;
}

const PREPARED_UUID_IN_PATH =
  /\/prepared-drops\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/i;

export function isPreparedDropUuidString(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    s.trim(),
  );
}

export { mergeCtx } from './siem-resolve-pick-targets.util';

function extractPreparedDropIdFromAction(action: string | null): DropTimelineContext {
  if (!action) return {};
  const m = action.match(PREPARED_UUID_IN_PATH);
  if (!m?.[1]) return {};
  return { preparedDropId: m[1] };
}

function contextFromRecord(o: Record<string, unknown>): DropTimelineContext {
  const uid =
    parseTimelineUniqueId(o.uniqueId) ?? parseTimelineUniqueId(o.unique_id);
  const wid = parseDropId(o.dropId) ?? parseDropId(o.id);
  return {
    ...(uid != null ? { uniqueId: uid } : {}),
    ...(wid != null ? { wiserDropId: wid } : {}),
  };
}

function mergeRecordInto(ctx: DropTimelineContext, body: unknown): DropTimelineContext {
  const o = asRecord(body);
  return o ? mergeCtx(ctx, contextFromRecord(o)) : ctx;
}

export function extractFromSiemDetailBodies(
  details: Record<string, unknown> | null,
): DropTimelineContext {
  if (!details) return {};
  let acc: DropTimelineContext = {};
  acc = mergeRecordInto(acc, details.requestBody);
  acc = mergeRecordInto(acc, details.responseBody);
  const res = asRecord(details.responseBody);
  if (res) acc = mergeRecordInto(acc, res.data);
  const req = asRecord(details.requestBody);
  if (req) acc = mergeRecordInto(acc, req.data);
  return acc;
}

export function extractFromDropAction(action: string | null): DropTimelineContext {
  if (!action || !action.includes('/smswiser/drops')) return {};
  const out: DropTimelineContext = {};
  const q = action.match(/[?&]unique(?:_?id)=([A-Za-z0-9-]{4,64})/i);
  if (q) out.uniqueId = q[1];
  const tail = action.split(/\/smswiser\/drops\/?/i)[1];
  if (!tail) return out;
  const pathOnly = tail.split(/[?#]/)[0];
  const first = pathOnly.split('/').filter(Boolean)[0];
  if (!first || RESERVED_DROP_PATH_SEGMENTS.has(first.toLowerCase())) return out;
  const uid = parseTimelineUniqueId(first);
  if (uid) out.uniqueId = uid;
  else {
    const wid = parseDropId(first);
    if (wid != null) out.wiserDropId = wid;
  }
  return mergeCtx(out, {});
}

export function extractDropTimelineContextFromSiem(
  details: Record<string, unknown> | null,
  action: string | null,
): DropTimelineContext {
  const kill = extractSmswiserKillContextFromSiem(details, action);
  const killNorm: DropTimelineContext = {
    ...(kill.uniqueId != null ? { uniqueId: parseTimelineUniqueId(kill.uniqueId) } : {}),
    ...(kill.wiserDropId != null ? { wiserDropId: kill.wiserDropId } : {}),
  };
  const bodies = details ? extractFromSiemDetailBodies(details) : {};
  const path = extractFromDropAction(action);
  const preparedFromAction = extractPreparedDropIdFromAction(action);
  const bulk = bulkPreparedContextFromDetails(details);
  const editBatch = editBatchContextFromDetails(details);
  let merged = mergeCtx(mergeCtx(mergeCtx(mergeCtx(killNorm, bodies), path), preparedFromAction), {});
  merged = mergeCtx(merged, bulk);
  merged = mergeCtx(merged, editBatch);
  return collapseSinglePickTarget(merged);
}
